/**
 * Opens a file dialog for image selection with callback support
 * @param callback - Function to call with file data including base64 and size
 */
export const openImageFileDialog = (
  callback: (
    file: { name: string; base64: string; size: number } | null
  ) => void
): void => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.multiple = false;

  input.onchange = (event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        callback({
          name: file.name,
          base64: reader.result as string,
          size: file.size,
        });
      };
      reader.onerror = () => {
        callback(null);
      };
      reader.readAsDataURL(file);
    } else {
      callback(null);
    }
  };

  input.oncancel = () => {
    callback(null);
  };

  // Trigger file dialog
  input.click();
};

/**
 * Converts file to base64 string
 * @param file - File to convert
 * @returns Promise that resolves to base64 string
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data:image/*;base64, prefix to get just the base64 data
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () =>
      reject(new Error('Failed to convert file to base64'));
    reader.readAsDataURL(file);
  });
};

/**
 * Reads the pixel dimensions of an image without uploading it.
 * @param source - data URL or object URL of the image
 * @returns Promise with the natural width/height, or null when unreadable
 */
export const getImageDimensions = (
  source: string
): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () =>
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve(null);
    image.src = source;
  });
};

/**
 * Validates if the file is a valid image
 * @param file - File to validate
 * @returns boolean indicating if file is valid
 */
export const validateImageFile = (file: File): boolean => {
  const allowedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
  ];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!allowedTypes.includes(file.type)) {
    return false;
  }

  if (file.size > maxSize) {
    return false;
  }

  return true;
};
